import { AssetType } from "../../schemas/AssetSchemas.ts";
import type {
  EstimationMethodCreateOneRequestType,
  EstimationMethodDeleteOneRequestType,
  EstimationMethodGetListRequestType,
  EstimationMethodGetOneRequestType,
  EstimationMethodType,
  EstimationMethodUpdateOneRequestType,
} from "../../schemas/EstimationMethodSchemas.ts";
import type { JobStatusType, JobType } from "../../schemas/JobSchemas.ts";
import type { ReportWithDownloadV2Type } from "../../schemas/ReportSchemas.ts";
import {
  Resources,
  getFilterObject,
  type CreateOneResponseType,
  type DeleteOneResponseType,
  type GetListResponseType,
  type GetOneResponseType,
  type ResourceAdapterToAdapterQueryKeysType,
  type ResourceAdapterType,
  type UpdateOneResponseType,
} from "../../util/index.ts";
import { restAPI } from "../api.ts";
import {
  EstimationMethodConfiguration,
  type EstimationMethodConfigurationDomainType,
} from "../EstimationMethodDomain/EstimationMethodConfiguration.ts";
import {
  EstimationMethodDefaultRecordDomain,
  type EstimationMethodDefaultRecordDomainType,
} from "../EstimationMethodDomain/EstimationMethodDefaultRecord.ts";
import {
  EstimationMethodInputSourceDomain,
  type EstimationMethodInputSourceDomainType,
} from "../EstimationMethodDomain/EstimationMethodInputSource.ts";
import {
  EstimationMethodRun,
  type EstimationMethodRunDomainType,
} from "../EstimationMethodDomain/EstimationMethodRun.ts";

export interface EstimationMethodAdapterType
  extends Pick<
    ResourceAdapterType<EstimationMethodType>,
    "getList" | "getOne" | "createOne" | "deleteOne" | "exportList"
  > {
  /**
   * Get a list of estimation methods based on filters
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/estimation_methods/search_estimation_methods
   * @returns the standard wrapped API response of this endpoint
   */
  getList: (
    params: EstimationMethodGetListRequestType
  ) => Promise<GetListResponseType<EstimationMethodType>>;
  /**
   * Get an estimation method under an entity
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/estimation_methods/get_estimation_method
   * @returns the standard wrapped API response of this endpoint
   */
  getOne: (
    params: EstimationMethodGetOneRequestType
  ) => Promise<GetOneResponseType<EstimationMethodType>>;
  /**
   * Create an estimation method under an entity
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/estimation_methods/add_equipment_estimation_method
   * @returns the standard wrapped API response of this endpoint
   */
  createOne: (
    params: EstimationMethodCreateOneRequestType
  ) => Promise<CreateOneResponseType<EstimationMethodType>>;
  /**
   * Update an estimation method under an entity
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/estimation_methods/delete_estimation_method
   * @returns the standard wrapped API response of this endpoint
   */
  updateOne: (
    params: EstimationMethodUpdateOneRequestType
  ) => Promise<UpdateOneResponseType<EstimationMethodType>>;
  updateAsync: (params: {
    data: {
      update: EstimationMethodUpdateOneRequestType["data"];
      merge_default_asset_values?: boolean;
    };
    meta?: {
      filter: EstimationMethodGetListRequestType["filters"] &
        Record<string, string>;
    };
  }) => Promise<{ status: JobStatusType; job: JobType }>;
  /**
   * Delete an estimation method under an entity
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/estimation_methods/delete_estimation_method
   * @returns the standard wrapped API response of this endpoint
   */
  deleteOne: (
    params: EstimationMethodDeleteOneRequestType
  ) => Promise<DeleteOneResponseType>;
  /**
   * Get a exported list of estimation methods based on filters
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/estimation_methods/export_estimation_methods
   */
  exportList: (
    params: EstimationMethodGetListRequestType
  ) => Promise<ReportWithDownloadV2Type>;
  configuration: EstimationMethodConfigurationDomainType;
  run: EstimationMethodRunDomainType;
  inputSource: EstimationMethodInputSourceDomainType;
  defaultRecords: EstimationMethodDefaultRecordDomainType;
}

export const EstimationMethodAdapter: EstimationMethodAdapterType = {
  getList: ({ filters, pageSize, sortBy, sortDirection, meta }) => {
    return restAPI.nodeAPI.POST({
      endpoint: `/estimation_methods/search`,
      body: {
        page_size: pageSize,
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: {
          ...getFilterObject(filters),
        },
      },
      ...(meta?.period ? { query: { period: meta.period } } : {}),
      ...(meta?.isExcelFile
        ? {
            headers: {
              Accept:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
            responseType: "arraybuffer",
          }
        : {}),
    });
  },
  getOne: ({
    id,
    meta = {
      entityType: AssetType.EQUIPMENT,
    },
  }) =>
    restAPI.nodeAPI
      .GET<EstimationMethodType>({
        endpoint: `/estimation_methods/${meta.entityType}/${id}`,
        ...(meta?.period
          ? {
              query: {
                period: meta.period,
              },
            }
          : {}),
      })
      .then((resp) => ({ data: resp })),
  createOne: ({
    data,
    meta = {
      entityType: AssetType.EQUIPMENT,
    },
  }) =>
    restAPI.nodeAPI
      .POST<EstimationMethodType>({
        endpoint: `/estimation_methods/${meta.entityType}`,
        body: data,
      })
      .then((resp) => ({ data: resp })),
  updateOne: ({
    id,
    data,
    meta = {
      entityType: AssetType.EQUIPMENT,
    },
  }) =>
    restAPI.nodeAPI
      .PUT<EstimationMethodType>({
        endpoint: `/estimation_methods/${meta.entityType}/${id}`,
        body: data,
      })
      .then((resp) => ({ data: resp })),
  updateAsync: ({
    data,
    meta = {
      filter: {},
    },
  }) =>
    restAPI.nodeAPI.POST({
      endpoint: `/estimation_methods/update_async`,
      version: 2,
      body: {
        update: data.update,
        merge_default_asset_values: data.merge_default_asset_values,
        filter: getFilterObject(meta.filter),
      },
    }),
  deleteOne: ({
    id,
    meta = {
      entityType: AssetType.EQUIPMENT,
    },
  }) =>
    restAPI.nodeAPI.DELETE({
      endpoint: `/estimation_methods/${meta.entityType}/${id}`,
    }),
  exportList: ({ filters, sortBy, sortDirection }) => {
    return restAPI.nodeAPI.POST<ReportWithDownloadV2Type>({
      endpoint: "/estimation_methods/export",
      body: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: getFilterObject(filters),
      },
    });
  },
  configuration: EstimationMethodConfiguration,
  run: EstimationMethodRun,
  inputSource: EstimationMethodInputSourceDomain,
  defaultRecords: EstimationMethodDefaultRecordDomain,
};

export const EstimationMethodAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<EstimationMethodAdapterType> =
  {
    getList: (params) =>
      [Resources.ESTIMATION_METHOD, "getList", params] as const,
    exportList: (params) =>
      [Resources.ESTIMATION_METHOD, "export", params] as const,
    getOne: (params) =>
      [Resources.ESTIMATION_METHOD, "getOne", params.id] as const,
    updateOne: (_) => [Resources.ESTIMATION_METHOD, "getList"] as const,
    updateAsync: (_) => [Resources.ESTIMATION_METHOD, "getList"] as const,
    createOne: (_) => [Resources.ESTIMATION_METHOD] as const,
    deleteOne: () => [Resources.ESTIMATION_METHOD] as const,
    configuration: {
      getList: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "configuration",
          "getList",
          params,
        ] as const,
      exportList: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "configuration",
          "export",
          params,
        ] as const,
      getOne: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "configuration",
          "getOne",
          params.id,
        ] as const,
      update: (_) => [Resources.ESTIMATION_METHOD, "configuration"] as const,
      applyDefaults: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "configuration",
          "applyDefaults",
          params.id,
        ] as const,
      bulkApplyDefaults: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "configuration",
          "bulkApplyDefaults",
          params,
        ] as const,
      bulkApplyDefaultsAsync: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "configuration",
          "bulkApplyDefaultsAsync",
          params,
        ] as const,
      applyDefaultsAndRunCalculationsAsync: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "configuration",
          "applyDefaultsAndRunCalculationsAsync",
          params,
        ] as const,
    },
    run: {
      getList: () => [Resources.ESTIMATION_METHOD, "run", "getList"] as const,
      getOne: (params) =>
        [Resources.ESTIMATION_METHOD, "run", "getOne", params.id] as const,
      create: () => [Resources.ESTIMATION_METHOD, "run"] as const,
      createMany: () => [Resources.ESTIMATION_METHOD, "run"] as const,
      bulkRun: () => [Resources.ESTIMATION_METHOD, "run"] as const,
      bulkRunAsync: () => [Resources.ESTIMATION_METHOD, "run"] as const,
    },
    inputSource: {
      getOne: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "getOne",
          params.id,
          "inputSource",
          params.meta,
        ] as const,
      update: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "getOne",
          params.id,
          "inputSource",
          params.meta,
        ] as const,
      delete: (params) =>
        [
          Resources.ESTIMATION_METHOD,
          "getOne",
          params.id,
          "inputSource",
          params.meta,
        ] as const,
    },
    defaultRecords: {
      getList: () =>
        [Resources.ESTIMATION_METHOD, "defaultRecords", "getList"] as const,
      create: () =>
        [Resources.ESTIMATION_METHOD, "defaultRecords", "create"] as const,
      update: (params) => {
        return [Resources.ESTIMATION_METHOD, "defaultRecords", params] as const;
      },
    },
  };
